import {
  WSActions,
  WSElement,
  WSForm,
  WSList,
  WSText
} from "@wingspanhq/fe-component-library";
import * as Yup from "yup";
import {
  ComplianceEntityUsTaxStatus,
  AcknowledgementName,
  AcknowledgementStatus,
  Payer,
  PayerStatus
} from "@wingspanhq/wingspan-api-sdk";
import { queriesAcknowledgements, queriesPayers } from "@query";
import { useUserId } from "../../../../query/hooks/helpers";
import { useQueryPrimaryComplianceEntity } from "../../../../shared/hooks/useQueryPrimaryComplianceEntity";
import { selectorComplianceEntityCountry } from "../../../../shared/selectors/selectorComplianceEntityCountry";
import { WSQueries } from "../../../../query/WSQuery";
import {
  ConsentFormType,
  getConsentForms
} from "../../components/CertifyTaxPayerInfo/getConsentForms";
import {
  FormW9Consent,
  getValidationSchemaFormW9
} from "../../components/CertifyTaxPayerInfo/FormW9Consent";
import {
  FormW8BenConsent,
  getValidationSchemaFormW8Ben
} from "../../components/CertifyTaxPayerInfo/FormW8BenConsent";
import {
  FormCanadaTaxPayerCertificationConsent,
  getValidationSchemaFormCanadaTaxPayerCertificationConsent
} from "../../components/CertifyTaxPayerInfo/FormCanadaTaxPayerCertificationConsent";
import {
  FormLocalTaxInformationConsent,
  getValidationSchemaFormLocalTaxPayerCertificationConsent
} from "../../components/CertifyTaxPayerInfo/FormLocalTaxInformationConsent";
import React, { ReactElement } from "react";
import { useWSMutation } from "../../../../query/helpers";
import { WSServiceError } from "../../../../utils/serviceHelper";
import { payersApi, personsApi } from "@api";
import { useSession } from "@hooks/useSession";
import { buildTaxInformationConsent } from "../../../../shared/utils/buildTaxInformationConsent";
import { selectorPersonDisplayName } from "../../../../shared/selectors/selectorPersonDisplayName";
import { acknowledgementVersions } from "../../../../shared/constants/acknowledgements";
import { ErrorAlert } from "@components/ErrorAlert";
import { selectorCounterpartyDisplayName } from "../../../../shared/selectors/selectorCounterpartyDisplayName";
import { FlowStep } from "../../../../shared/components/FlowSwitch/types";

function useConsentForms(props: {
  payers: Payer[];
  payeeCountry: string;
  onNext?: () => void;
  isInternationalUSPerson: boolean;
}) {
  const userId = useUserId();
  const { personId } = useSession();
  // Fetching these acknowledgements to pre-fill the form
  const queryW9CertificationAcknowledgement =
    queriesAcknowledgements.getAcknowledgement.useQuery({
      acknowledgementName: AcknowledgementName.W9_CERTIFICATION
    });
  const queryW8BenCertificationAcknowledgement =
    queriesAcknowledgements.getAcknowledgement.useQuery({
      acknowledgementName: AcknowledgementName.W8_BEN_CERTIFICATION
    });
  const queryCanadianTaxPayerCertificationAcknowledgement =
    queriesAcknowledgements.getAcknowledgement.useQuery({
      acknowledgementName: AcknowledgementName.CANADIAN_TAX_PAYER_CERTIFICATION
    });
  const queryLocalTaxPayerCertificationAcknowledgement =
    queriesAcknowledgements.getAcknowledgement.useQuery({
      acknowledgementName: AcknowledgementName.LOCAL_TAX_PAYER_CERTIFICATION
    });
  const [acceptAcknowledgement] =
    queriesAcknowledgements.acceptAcknowledgement.useMutation();

  const getPayerNames = (payers: Payer[]) =>
    payers.map(payer => selectorCounterpartyDisplayName(payer));

  const { consentForms, taxInfoSharedPayers } = getConsentForms(
    props.payers,
    props.payeeCountry,
    props.isInternationalUSPerson
  );

  // Contractors who are not from the US, not from Canada and are not US persons
  // get expanded tax information sharing consent copy (PLA-3446)
  const isInternationalNonCanadaPayee =
    props.payeeCountry !== "US" &&
    props.payeeCountry !== "CA" &&
    !props.isInternationalUSPerson;

  const renderConsentForms = () => {
    const consentFormTypeToComponent: Record<ConsentFormType, ReactElement> = {
      [ConsentFormType.W9]: (
        <FormW9Consent
          payerNames={getPayerNames(taxInfoSharedPayers[ConsentFormType.W9])}
        />
      ),
      [ConsentFormType.W8Ben]: (
        <FormW8BenConsent
          payerNames={getPayerNames(taxInfoSharedPayers[ConsentFormType.W8Ben])}
          isInternationalNonCanadaPayee={isInternationalNonCanadaPayee}
        />
      ),
      [ConsentFormType.CanadaTaxPayerCertification]: (
        <FormCanadaTaxPayerCertificationConsent
          payerNames={getPayerNames(
            taxInfoSharedPayers[ConsentFormType.CanadaTaxPayerCertification]
          )}
        />
      ),
      [ConsentFormType.LocalTaxInformation]: (
        <FormLocalTaxInformationConsent
          payerNames={getPayerNames(
            taxInfoSharedPayers[ConsentFormType.LocalTaxInformation]
          )}
          isInternationalNonCanadaPayee={isInternationalNonCanadaPayee}
        />
      )
    };
    return (
      <WSList gap="XL">
        {consentForms.map(
          consentFormType => consentFormTypeToComponent[consentFormType]
        )}
      </WSList>
    );
  };

  const getValidationSchema = () => {
    const validationSchemas: Record<ConsentFormType, any> = {
      [ConsentFormType.W9]: getValidationSchemaFormW9(
        getPayerNames(taxInfoSharedPayers[ConsentFormType.W9])
      ),
      [ConsentFormType.W8Ben]: getValidationSchemaFormW8Ben(
        getPayerNames(taxInfoSharedPayers[ConsentFormType.W8Ben])
      ),
      [ConsentFormType.CanadaTaxPayerCertification]:
        getValidationSchemaFormCanadaTaxPayerCertificationConsent(
          getPayerNames(
            taxInfoSharedPayers[ConsentFormType.CanadaTaxPayerCertification]
          )
        ),
      [ConsentFormType.LocalTaxInformation]:
        getValidationSchemaFormLocalTaxPayerCertificationConsent(
          getPayerNames(
            taxInfoSharedPayers[ConsentFormType.LocalTaxInformation]
          )
        )
    };
    return consentForms
      .map(consentFormType => validationSchemas[consentFormType])
      .reduce(
        (finalSchema, curSchema) => finalSchema.concat(curSchema),
        Yup.object().shape({})
      );
  };

  const getDefaultValues = () => {
    const defaultValues: Record<ConsentFormType, any> = {
      [ConsentFormType.W9]: {
        w9Consent:
          queryW9CertificationAcknowledgement.data?.acknowledgementStatus ===
          AcknowledgementStatus.GIVEN,
        shareW9: false
      },
      [ConsentFormType.W8Ben]: {
        w8BenConsent:
          queryW8BenCertificationAcknowledgement.data?.acknowledgementStatus ===
          AcknowledgementStatus.GIVEN,
        shareW8Ben: false
      },
      [ConsentFormType.CanadaTaxPayerCertification]: {
        canadaTaxInfoConsent:
          queryCanadianTaxPayerCertificationAcknowledgement.data
            ?.acknowledgementStatus === AcknowledgementStatus.GIVEN,
        shareCanadaTaxInfo: false
      },
      [ConsentFormType.LocalTaxInformation]: {
        localTaxInfoConsent:
          queryLocalTaxPayerCertificationAcknowledgement.data
            ?.acknowledgementStatus === AcknowledgementStatus.GIVEN,
        shareLocalTaxInfo: false
      }
    };

    return consentForms
      .map(ConsentFormType => defaultValues[ConsentFormType])
      .reduce((finalDefaultValues, curDefaultValues) => {
        return {
          ...finalDefaultValues,
          ...curDefaultValues
        };
      }, {});
  };

  const [submit, submitMeta] = useWSMutation<any, WSServiceError>(
    async () => {
      // Legacy way of updating user activity for consent forms
      // await usersService.activity.update(userId, {
      //   events: {
      //     w9FormAcknowledged: new Date()
      //   }
      // });

      const consentFormTypeToAcknowledgementNameMap: Record<
        ConsentFormType,
        AcknowledgementName
      > = {
        [ConsentFormType.W9]: AcknowledgementName.W9_CERTIFICATION,
        [ConsentFormType.W8Ben]: AcknowledgementName.W8_BEN_CERTIFICATION,
        [ConsentFormType.CanadaTaxPayerCertification]:
          AcknowledgementName.CANADIAN_TAX_PAYER_CERTIFICATION,
        [ConsentFormType.LocalTaxInformation]:
          AcknowledgementName.LOCAL_TAX_PAYER_CERTIFICATION
      };

      // Update acknowledgement for consent forms
      console.log("consentForms", consentForms);
      await Promise.all(
        consentForms.map(consentFormType =>
          acceptAcknowledgement({
            acknowledgementName:
              consentFormTypeToAcknowledgementNameMap[consentFormType],
            acceptAcknowledgementRequest: {
              version:
                acknowledgementVersions[
                  consentFormTypeToAcknowledgementNameMap[consentFormType]
                ]
            }
          })
        )
      );

      // Share tax information with each payer via v3 shareTaxInformation.
      // Payer ids come from v3 listPayers (`Payer.id`) — the v3 verb only resolves
      // v3 ids, not the disjoint v1 payer-row ids this step used to feed.
      // INTERIM consent: signature = payee's on-file legal name, no dedicated
      // e-signature capture yet; pending Legal sign-off (see buildTaxInformationConsent).
      const person = await personsApi.getPerson({ personId });
      const consent = buildTaxInformationConsent({
        signature: selectorPersonDisplayName(person)
      });
      await Promise.all(
        props.payers.map(payer =>
          payersApi.shareTaxInformation({
            payerId: payer.id ?? "",
            payerShareTaxInformationRequest: { consent }
          })
        )
      );
    },
    {
      dependencies: [
        queriesAcknowledgements.getAcknowledgement.queryKeyBase,
        queriesPayers.listPayers.queryKeyBase
      ],
      onSuccess: () => {
        props.onNext?.();
      }
    }
  );

  return {
    consentFormsCount: consentForms.length,
    renderConsentForms,
    getValidationSchema,
    getDefaultValues,
    taxInfoSharedPayers,
    submit,
    submitMeta
  };
}

export const CertifyTaxPayerInfo: React.FC<FlowStep> = ({ onNext }) => {
  const queryAllPayerList = queriesPayers.listPayers.useQueryAll({
    filter: {
      status: { eq: PayerStatus.ACTIVATED }
    }
  });

  const queryCustomerEntity = useQueryPrimaryComplianceEntity();

  const {
    consentFormsCount,
    renderConsentForms,
    getValidationSchema,
    getDefaultValues,
    submit,
    submitMeta
  } = useConsentForms({
    payers: queryAllPayerList.flatData || [],
    payeeCountry:
      selectorComplianceEntityCountry(queryCustomerEntity.data) ?? "US",
    onNext,
    isInternationalUSPerson:
      selectorComplianceEntityCountry(queryCustomerEntity.data) !== "US" &&
      queryCustomerEntity.data?.usTaxStatus ===
        ComplianceEntityUsTaxStatus.US_PERSON
  });

  const onSubmit = async (data: any) => {
    console.log("form submit data", data);
    await submit();
  };

  return (
    <>
      {consentFormsCount > 1 ? (
        <WSElement>
          <WSText.Paragraph weight="medium" color="gray700" mb="M">
            Certify taxpayer information
          </WSText.Paragraph>
          <WSText.ParagraphSm weight="book" color="gray500" mb="2XL">
            Please review and certify your taxpayer information is accurate.
            Separate certifications may be required if your clients are located
            in different countries.
          </WSText.ParagraphSm>
        </WSElement>
      ) : null}

      <WSQueries
        queries={{
          queryAllPayerList,
          queryCustomerEntity
        }}
      >
        {() => {
          const defaultValues = getDefaultValues();
          const validationSchema = getValidationSchema();
          return (
            <WSForm
              defaultValues={defaultValues}
              validationSchema={validationSchema}
              onSubmit={onSubmit}
            >
              <WSList gap="2XL">
                {renderConsentForms()}

                <ErrorAlert
                  error={submitMeta.error}
                  context="PayeeOnboardingConsentForms"
                />

                <WSForm.Values names={Object.keys(defaultValues)}>
                  {(values: Record<string, boolean>) => {
                    const isValid = validationSchema.isValidSync(values);

                    return (
                      <WSActions
                        alignment="fill"
                        buttons={[
                          {
                            label: "Continue",
                            disabled: !isValid,
                            type: "submit",
                            loading: submitMeta.isLoading
                          }
                        ]}
                      />
                    );
                  }}
                </WSForm.Values>

                <WSText.ParagraphXs color="gray400">
                  The information on this page is required to setup your
                  Wingspan account, if you exit or leave without completing this
                  step, you will be required to do this later.
                </WSText.ParagraphXs>
              </WSList>
            </WSForm>
          );
        }}
      </WSQueries>
    </>
  );
};
